import { Button, CircleProgress } from '@devrangex/rui'
import styled from '@emotion/styled'
import { useCallback, useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'

import { BayGridArray, bayGridSizeState, bayGridState, bayMachinesState } from '../../../store'
import { qrBoardState } from '../../../store/qr'
import { bayManager } from '../../../utils/socket/manager'
import useBaySocket from '../../hooks/useBaySocket'

import QrBoard from './QrBoard'
import BayItem from './item/BayItem'


interface BayGridProps {
  socketURL?: string
}

const BayGridContainer = styled.div<{ x?: number }>`
    display: grid;
    grid-template-columns: repeat(${({x}) => x}, 1fr);
    gap: 10px;
    height: 760px;
`
const BayGridCol = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const FallBackContainer = styled.div`
    margin: 20vh auto 0 auto;
    text-align: center;
    max-width: 520px;

    > p {
        ${({theme}) => theme.font.heading3};
    }
`

/**
 * 타석 렌더관리 훅
 */
const useBayRenderer = (socketURL?: string) => {
  const [bayGrid, setBayGrid] = useRecoilState(bayGridState)
  const [bayGridSize, setBayGridSize] = useRecoilState(bayGridSizeState)
  const [machines, setMachines] = useRecoilState(bayMachinesState)
  const [bayTimer, setBayTimer] = useState<NodeJS.Timeout>()

  const {connect, io, isConnected, isConnecting} = useBaySocket(socketURL)

  useEffect(() => {
    bayManager.gridSizeSetter = setBayGridSize
    bayManager.gridMachineSetter = setMachines
  }, [setBayGridSize, setMachines])

  // 타석 연결
  useEffect(() => {
    if (!io) return

    connect(() => {
      setBayTimer(
        setInterval(() => {
          bayManager.setMachineTimer()
        }, 1000)
      )
    })
  }, [connect, io])

  // 타석 배치
  useEffect(() => {
    if (!bayGridSize.isValid || machines.length < 1) return

    if (bayGridSize.countOfItems < machines.length) return

    const bayGrid = bayGridSize.getEmptyGrid()

    for (const machine of machines) {
      if (bayGridSize.width < machine.gridX) return
      
      // @ts-ignore
      bayGrid[machine.gridX - 1][machine.gridY - 1] = machine
    }

    setBayGrid([...(bayGrid as BayGridArray)])
  }, [machines, bayGridSize, setBayGrid])

  // 타석 라이프사이클
  useEffect(() => {
    const handleOnline = () => {
      location.reload()
    }

    addEventListener('online', handleOnline)

    return () => {
      setBayGrid([[]])
      clearInterval(bayTimer)
      io?.disconnect()

      removeEventListener('online', handleOnline)
    }
  }, [])

  return {
    bayGrid,
    bayGridSize,
    isConnected,
    isConnecting,
    hasMachines: machines.length > 0,
    baySocket: io,
  }
}

export default function BayGrid({socketURL}: BayGridProps) {
  const {
    bayGrid,
    bayGridSize,
    isConnecting,
    isConnected,
    hasMachines,
    baySocket,
  } = useBayRenderer(socketURL)
  const [qrBoard, setQrBoard] = useRecoilState(qrBoardState)

  const onCloseQrBoard = useCallback(() => {
    setQrBoard({
      visible: false,
      loginCode: undefined,
      machineName: undefined,
    })
  }, [setQrBoard])

  if (isConnecting) {
    return (
      <FallBackContainer>
        <CircleProgress size="lg"/>
      </FallBackContainer>
    )
  }

  if (!isConnected) {
    return (
      <FallBackContainer>
        <p>타석과 연결이 끊어졌습니다.</p>
        <Button size="sm" onClick={() => baySocket?.connect()}>
          연결재시도
        </Button>
      </FallBackContainer>
    )
  }

  if (!hasMachines) {
    return <FallBackContainer>타석이 존재하지 않습니다</FallBackContainer>
  }

  return (
    <>
      <BayGridContainer x={bayGridSize.width} role="grid">
        {bayGrid.map((cols, x) => (
          <BayGridCol key={`col-${x}`}>
            {cols.map((bayItem, y) => {
              const cellAddr = `cell:${x + 1}-${y + 1}`

              return (
                <BayItem
                  bayMachine={bayItem}
                  role="gridcell"
                  key={cellAddr}
                  bayItemSize={getBayItemSize(bayGridSize.width)}
                  data-testid={cellAddr}
                />
              )
            })}
          </BayGridCol>
        ))}
      </BayGridContainer>

      <QrBoard
        visible={qrBoard.visible}
        onClose={onCloseQrBoard}
        machineName={qrBoard.machineName}
        loginCode={qrBoard.loginCode}
      />
    </>
  )
}

function getBayItemSize(gridWidth: number) {
  if (gridWidth === 10) return 'sm'
  if (gridWidth === 6) return 'lg'
  return 'md'
}
